/**
 * TrackedFilmsService
 * Provides unified access to films available in the Film Tracking dashboard.
 * Uses the GET /api/v1/films endpoint to get all tracked films.
 * Used primarily for film comp search/selection workflows.
 */

import apiService from './apiService';
import { parseDateFromAPI } from '../utils/dateUtils';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FILM_TRACKING_LIMIT = 10000; // Increased to ensure we get all films
const LEGACY_PAGE_SIZE = 100;

let cachedFilms = [];
let cacheTimestamp = 0;
let inFlightPromise = null;

const normaliseFilm = (film) => {
  // Handle different possible field names from the API
  const imdbId = film.imdb_id || film.imdbId || film.tt_id;
  const title = film.film_title || film.title_text || film.title || film.film_name || 'Untitled Film';
  const releaseDate = parseDateFromAPI(film.release_date || film.releaseDate);

  if (!imdbId || !title) {
    return null;
  }

  return {
    imdbId,
    title,
    alternateTitles: film.title_text && film.title_text !== title ? [film.title_text] : [],
    releaseDate: releaseDate ? releaseDate.toISOString().split('T')[0] : null,
    releaseYear: releaseDate ? releaseDate.getFullYear() : film.release_year || film.releaseYear || null,
    source: 'film_tracking',
    sourceLabel: 'Film Tracking',
    metadata: {
      weeksUntilRelease: film.weeksUntilRelease ?? film.current_weeks_until_release ?? film.weeks_until_release ?? null,
      surveyDate: film.survey_date || film.last_survey_date || film.surveyDate || null
    }
  };
};

const loadFilms = async () => {
  try {
    // Try the new /api/v1/films endpoint first
    let response;
    let films = [];
    
    try {
      // Try with limit first, but also try without limit if we get 0 results
      response = await apiService.getFilms({
        limit: FILM_TRACKING_LIMIT
      });
      
      // If we got 0 films, try without limit parameter
      const filmsFromResponse = Array.isArray(response?.data) 
        ? response.data 
        : Array.isArray(response?.films)
        ? response.films
        : [];
      
      if (filmsFromResponse.length === 0) {
        console.log('⚠️ Got 0 films with limit parameter, trying without limit...');
        response = await apiService.getFilms({});
      }
      
      // Log the full response structure for debugging
      console.log('🔍 Full API response structure:', {
        hasSuccess: 'success' in response,
        success: response?.success,
        hasData: 'data' in response,
        dataType: Array.isArray(response?.data) ? 'array' : typeof response?.data,
        dataLength: Array.isArray(response?.data) ? response.data.length : 'N/A',
        hasFilms: 'films' in response,
        filmsType: Array.isArray(response?.films) ? 'array' : typeof response?.films,
        filmsLength: Array.isArray(response?.films) ? response.films.length : 'N/A',
        responseKeys: Object.keys(response || {}),
        sampleResponse: JSON.stringify(response).substring(0, 500)
      });
      
      // Handle different possible response structures
      if (Array.isArray(response?.data)) {
        films = response.data;
      } else if (Array.isArray(response?.films)) {
        films = response.films;
      } else if (response?.data && typeof response.data === 'object') {
        // If data is an object, check if it has a films array
        if (Array.isArray(response.data.films)) {
          films = response.data.films;
        } else if (Array.isArray(response.data.data)) {
          films = response.data.data;
        } else {
          // Try to extract films from nested structure
          console.log('⚠️ Unexpected response.data structure:', response.data);
          films = [];
        }
      } else {
        console.log('⚠️ No films array found in response');
        films = [];
      }
      
      console.log('✅ Loaded films from /api/v1/films:', {
        total: films.length,
        paginationTotal: response.pagination?.total || response?.data?.pagination?.total
      });
    } catch (filmsError) {
      // If /api/v1/films doesn't exist (404), fall back to film-tracking-summary
      if (filmsError.status === 404 || filmsError.message?.includes('404') || filmsError.message?.includes('not found')) {
        console.log('⚠️ /api/v1/films endpoint not available, falling back to film-tracking-summary endpoint');
        
        // Use the film tracking summary endpoint as fallback
        // Use a very wide date range to get all films (past 2 years to future 2 years)
        const now = new Date();
        const twoYearsAgo = new Date(now);
        twoYearsAgo.setFullYear(now.getFullYear() - 2);
        const twoYearsFromNow = new Date(now);
        twoYearsFromNow.setFullYear(now.getFullYear() + 2);
        
        const summaryResponse = await apiService.getFilmTrackingSummary({
          limit: FILM_TRACKING_LIMIT,
          release_date_from: twoYearsAgo.toISOString().split('T')[0],
          release_date_to: twoYearsFromNow.toISOString().split('T')[0],
          sort_by: 'release_date',
          sort_order: 'asc'
        });
        
        // Extract films from the summary response
        // The response structure may vary, so we handle different formats
        if (Array.isArray(summaryResponse?.data)) {
          films = summaryResponse.data;
        } else if (summaryResponse?.data?.films && Array.isArray(summaryResponse.data.films)) {
          films = summaryResponse.data.films;
        } else if (summaryResponse?.films && Array.isArray(summaryResponse.films)) {
          films = summaryResponse.films;
        }
        
        console.log('✅ Loaded films from film-tracking-summary (fallback):', {
          total: films.length,
          paginationTotal: summaryResponse.pagination?.total
        });
      } else {
        // Re-throw if it's a different error
        throw filmsError;
      }
    }
    
    console.log('📊 Sample films loaded:', films.slice(0, 5).map(f => ({
      imdbId: f.imdb_id || f.imdbId || f.tt_id,
      title: f.film_title || f.title_text || f.title || f.film_name
    })));
    
    const normalized = films
      .map(normaliseFilm)
      .filter(Boolean);
    
    // Check if specific film exists
    const venomFilm = normalized.find(f => f.imdbId === 'tt16366836');
    if (venomFilm) {
      console.log('✅ Found Venom: The Last Dance in loaded films:', venomFilm);
    } else {
      console.log('❌ Venom: The Last Dance (tt16366836) NOT found in loaded films');
      console.log('📋 Checking raw API response for this ID...');
      const rawVenom = films.find(f => 
        (f.imdb_id || f.imdbId || f.tt_id || '').toLowerCase() === 'tt16366836'
      );
      if (rawVenom) {
        console.log('🔍 Found raw film data:', rawVenom);
        console.log('🔍 Normalized would be:', normaliseFilm(rawVenom));
      } else {
        console.log('❌ Film not found in raw API response either');
        console.log('📊 Total films in response:', films.length);
        console.log('📋 Sample IMDB IDs:', films.slice(0, 20).map(f => f.imdb_id || f.imdbId || f.tt_id));
      }
    }
    
    return normalized;
  } catch (error) {
    console.error('❌ Failed to load tracked films:', error);
    return [];
  }
};

const buildCache = async () => {
  const films = await loadFilms();

  // Remove duplicates by imdbId
  const filmsById = new Map();
  films.forEach((film) => {
    if (!filmsById.has(film.imdbId)) {
      filmsById.set(film.imdbId, film);
    }
  });

  cachedFilms = Array.from(filmsById.values()).map((film) => {
    const titleLower = film.title.toLowerCase();
    // Create search tokens that include:
    // 1. Full title
    // 2. Words from title (split on spaces, colons, hyphens, etc.)
    // 3. Alternate titles
    // 4. IMDB ID
    const titleWords = titleLower
      .split(/[\s:,\-–—]+/)
      .filter(word => word.length > 0);
    
    const searchTokens = [
      titleLower,
      ...titleWords,
      ...(film.alternateTitles || []).map((alt) => alt.toLowerCase()),
      ...(film.alternateTitles || []).flatMap((alt) => 
        alt.toLowerCase().split(/[\s:,\-–—]+/).filter(word => word.length > 0)
      ),
      film.imdbId.toLowerCase()
    ];
    
    return {
      ...film,
      titleLower,
      searchTokens: [...new Set(searchTokens)] // Remove duplicates
    };
  });
  cacheTimestamp = Date.now();
};

const ensureCache = async (forceRefresh = false) => {
  const cacheExpired = Date.now() - cacheTimestamp > CACHE_TTL_MS;

  if (!forceRefresh && cachedFilms.length > 0 && !cacheExpired) {
    return cachedFilms;
  }

  if (!inFlightPromise) {
    inFlightPromise = buildCache()
      .catch((error) => {
        console.error('❌ Failed to refresh tracked films cache:', error);
      })
      .finally(() => {
        inFlightPromise = null;
      });
  }

  await inFlightPromise;
  return cachedFilms;
};

const scoreMatch = (film, query) => {
  // Exact title match at start (best match)
  const titleIndex = film.titleLower.indexOf(query);
  if (titleIndex === 0) return 0;
  if (titleIndex > 0) return titleIndex + 1;

  // Check if any search token starts with the query (good match)
  const tokenStartsWith = film.searchTokens.find((token) => token.startsWith(query));
  if (tokenStartsWith) {
    return 50 + tokenStartsWith.indexOf(query);
  }

  // Check if any search token contains the query (partial match)
  if (film.searchTokens.some((token) => token.includes(query))) {
    return 100;
  }

  // No match
  return 1000;
};

export const searchTrackedFilms = async (query, options = {}) => {
  const trimmedQuery = (query || '').trim().toLowerCase();
  if (trimmedQuery.length < 2) {
    return [];
  }

  const films = await ensureCache(options.forceRefresh);

  const matched = films
    .map((film) => ({
      ...film,
      matchScore: scoreMatch(film, trimmedQuery)
    }))
    .filter((film) => film.matchScore < 1000)
    .sort((a, b) => a.matchScore - b.matchScore || a.title.localeCompare(b.title));

  const limit = options.limit ?? 25;
  return matched.slice(0, limit).map((film) => ({
    imdbId: film.imdbId,
    title: film.title,
    releaseDate: film.releaseDate,
    releaseYear: film.releaseYear,
    source: film.source,
    sourceLabel: film.sourceLabel,
    metadata: film.metadata
  }));
};

export const getTrackedFilms = async (options = {}) => {
  const films = await ensureCache(options.forceRefresh);
  return films.map((film) => ({
    imdbId: film.imdbId,
    title: film.title,
    releaseDate: film.releaseDate,
    releaseYear: film.releaseYear,
    source: film.source,
    sourceLabel: film.sourceLabel,
    metadata: film.metadata
  }));
};

/**
 * Debug function to check if a specific film exists in the cache
 * @param {string} imdbId - IMDB ID to search for
 * @returns {Promise<Object|null>} - Film data if found, null otherwise
 */
export const findFilmByImdbId = async (imdbId) => {
  const films = await ensureCache();
  const film = films.find(f => f.imdbId.toLowerCase() === imdbId.toLowerCase());
  
  if (film) {
    console.log('✅ Film found in cache:', {
      imdbId: film.imdbId,
      title: film.title,
      titleLower: film.titleLower,
      searchTokens: film.searchTokens,
      releaseDate: film.releaseDate,
      releaseYear: film.releaseYear
    });
    return {
      imdbId: film.imdbId,
      title: film.title,
      releaseDate: film.releaseDate,
      releaseYear: film.releaseYear,
      source: film.source,
      sourceLabel: film.sourceLabel,
      metadata: film.metadata
    };
  } else {
    console.log('❌ Film NOT found in cache:', imdbId);
    console.log('📊 Total films in cache:', films.length);
    console.log('📋 Sample film IDs:', films.slice(0, 10).map(f => f.imdbId));
    return null;
  }
};

/**
 * Debug function to test search with a query
 * @param {string} query - Search query
 * @returns {Promise<Object>} - Search results with debug info
 */
export const debugSearch = async (query) => {
  const trimmedQuery = (query || '').trim().toLowerCase();
  console.log('🔍 Debug search for query:', trimmedQuery);
  
  const films = await ensureCache();
  console.log('📊 Total films in cache:', films.length);
  
  const matched = films
    .map((film) => ({
      ...film,
      matchScore: scoreMatch(film, trimmedQuery)
    }))
    .filter((film) => film.matchScore < 1000)
    .sort((a, b) => a.matchScore - b.matchScore || a.title.localeCompare(b.title));
  
  console.log('🎯 Matched films:', matched.length);
  matched.slice(0, 5).forEach(film => {
    console.log('  -', film.title, `(score: ${film.matchScore}, imdb: ${film.imdbId})`);
  });
  
  return {
    query: trimmedQuery,
    totalFilms: films.length,
    matchedCount: matched.length,
    results: matched.slice(0, 25)
  };
};

export default {
  searchTrackedFilms,
  getTrackedFilms,
  findFilmByImdbId,
  debugSearch
};

